import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-home">← Back to Home</Link>

        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-last-updated">Last updated: September 30, 2026</p>

        <div className="legal-content">
          <p>
            Welcome to Dangol! These Terms of Service ("Terms") govern your use of the Dangol mobile applications and any related services (collectively, the "Service") provided by Dangol LLC ("Dangol," "we," "us," or "our").
          </p>

          <p>
            By creating an account or using our Service, you agree to be bound by these Terms.
          </p>

          <h2>1. Definitions</h2>
          <p>
            "Service" refers to the Dangol customer-facing mobile application (the "App") and all related technologies and platforms used by the Customer.
          </p>
          <p>
            "Customer" refers to an individual user of the App who collects rewards and participates in Events.
          </p>
          <p>
            "Business" or "Shop" refers to a participating business entity that offers a loyalty program and/or hosts Events to Customers through the Dangol platform.
          </p>
          <p>
            "Event Coordinator" refers to a Business or organizer that creates and hosts Events (free or paid) through the Dangol platform. References to a "Business" in these Terms include Event Coordinators where applicable.
          </p>
          <p>
            "Event" refers to an event listed on the Service by a Business or Event Coordinator. "Paid Event" refers to an Event that requires a payment, such as a ticket, to attend.
          </p>
          <p>
            "User" or "Customer" refers to an individual user of the App who collects rewards. (If you are a Business, your use of the platform is governed by the separate Dangol Business Terms of Service.)
          </p>

          <h2>2. Description of Service</h2>
          <p>Dangol is a digital loyalty and community platform that enables Businesses to offer rewards programs and host Events for their Customers. The Service includes:</p>
          <ul>
            <li>Digital stamp collection, reward tracking, and receiving notifications from Shops.</li>
            <li>Discovering, RSVPing to, and attending Events hosted by Businesses and Event Coordinators, including free and Paid Events.</li>
          </ul>

          <h2>3. User Accounts</h2>
          <p>
            To use the Service, you must create an account. You agree to provide accurate and current information and are responsible for safeguarding your password and for all activities that occur under your account.
          </p>
          <p>
            <strong>Usernames:</strong> When you create an account, we automatically assign you a username consisting of a first initial and random numbers, which is anonymous by default. You may change your username, subject to certain restrictions (for example, we do not permit profanity or offensive terms). If you choose a username that identifies you, that is your choice.
          </p>

          <h2>4. Rewards Program</h2>
          <p>Dangol provides the platform for the loyalty program, but the rewards themselves are offered directly by the individual Shops.</p>
          <p>
            <strong>Responsibility:</strong> The terms, conditions, fulfillment, and value of any reward are determined solely by the participating Business. Dangol is not a party to the transaction between the Customer and the Business and is not responsible for a Business's failure to honor a reward.
          </p>
          <p>
            <strong>Rule Changes:</strong> A Business may change its reward rules at any time. The Dangol service is designed to honor the rules that were in effect for a Customer at the start of their current reward cycle. Rewards have no cash value and are not transferable.
          </p>

          <h2>5. Events</h2>
          <p>
            Dangol provides a platform for Businesses and Event Coordinators to list, promote, and manage Events. Dangol does not itself host, organize, or run Events.
          </p>
          <p>
            <strong>Responsibility:</strong> The content, scheduling, pricing, admission, safety, cancellation, and conduct of any Event are the sole responsibility of the Business or Event Coordinator hosting it. Dangol is not a party to, and is not responsible for, any Event or any interaction between you and an Event Coordinator. RSVP availability, capacity, and whether your attendance is confirmed are determined by the hosting Business or Event Coordinator.
          </p>
          <p>
            <strong>Assumption of Risk:</strong> You attend Events at your own risk. Live and in-person Events may carry inherent risks, including the risk of illness, bodily injury, disability, or death. By choosing to RSVP to or attend an Event, you freely and knowingly assume all such risks, and you release Dangol from, and agree not to hold Dangol liable for, any injury, loss, or damage arising out of or related to your attendance at or participation in any Event.
          </p>

          <h2>6. Payments, Refunds, and Cancellations</h2>
          <p>Some Events require a payment to attend ("Paid Events").</p>
          <p>
            <strong>Payments:</strong> Payments for Paid Events are processed by a third-party payment processor (such as Stripe) and are made directly to the Business or Event Coordinator hosting the Event. Dangol does not collect, hold, process, or control funds for Paid Events, and is not the merchant or seller of any ticket or admission. Prices, applicable taxes, and any processing fees are set by the hosting Business or Event Coordinator and/or the payment processor, and may be shown to you before you complete a purchase.
          </p>
          <p>
            <strong>Refunds and Cancellations:</strong> All refunds, exchanges, cancellations, and related disputes for any Paid Event are handled solely between you and the Business or Event Coordinator that hosted the Event. Dangol does not issue refunds, is not responsible for any Business's or Event Coordinator's refund or cancellation policies, and has no obligation to mediate or resolve payment disputes. Any request for a refund, or any question about a charge, must be directed to the Business or Event Coordinator directly.
          </p>

          <h2>7. Event Coordinators and Your Information</h2>
          <p>
            When you RSVP to or attend an Event, the hosting Business or Event Coordinator does not receive your personal contact information (such as your email address or phone number) from Dangol, except where you win a raffle or promotion as described in Section 9 (Raffles and Promotions). Event Coordinators otherwise receive only aggregate, non-identifying information and trends about their Events, such as overall attendance and engagement.
          </p>
          <p>
            Your username may be visible to other users in social features of the Service, such as Event RSVP lists and community message boards. Because your username is anonymous by default (see Section 3), whether it identifies you is your choice. For details on how we collect, use, and protect your information, please see our Privacy Policy.
          </p>

          <h2>8. Notifications</h2>
          <p>Businesses and Event Coordinators may send promotional and event-related notifications to their Customers through the Service.</p>
          <p>
            <strong>Customer Choice:</strong> Customers can opt out of receiving promotional notifications from any specific Shop or Event Coordinator at any time through the settings in the Customer App.
          </p>

          <h2>9. Raffles and Promotions</h2>
          <p>
            From time to time, Businesses, Event Coordinators, or Dangol may offer raffles, giveaways, or other promotions through the Service (each, a "Promotion").
          </p>
          <p>
            <strong>Sponsor Responsibility:</strong> Each Promotion is offered by the Business, Event Coordinator, or Dangol identified as its sponsor, who is responsible for the Promotion, including the prize, eligibility, selection of winners, and delivery of the prize. Any specific or official rules provided for a Promotion apply in addition to these Terms and control in the event of a conflict.
          </p>
          <p>
            <strong>Participation:</strong> Participation is subject to the rules of the specific Promotion and to applicable law. Entries and prizes have no cash value, are non-transferable, and may be subject to eligibility requirements. Promotions are void where prohibited.
          </p>
          <p>
            <strong>Winners:</strong> If you win a Promotion, we may share the information reasonably necessary to deliver your prize — which may include your name and email address — with the Business or Event Coordinator sponsoring it, so that they can contact you and provide the prize. You may be required to confirm your win within a stated period or forfeit the prize.
          </p>

          <h2>10. User Conduct</h2>
          <p>
            You agree not to use the Service for any unlawful purpose or in any way that could damage, disable, or impair the Service.
          </p>

          <h2>11. Intellectual Property</h2>
          <p>
            The Service and its original content, features, and functionality are owned by Dangol and are protected by international copyright, trademark, and other intellectual property laws.
          </p>

          <h2>12. Disclaimers</h2>
          <p>
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis. Dangol makes no warranties, expressed or implied, regarding the reliability, security, or availability of the Service. Dangol does not host Events, sell tickets, or process payments, and makes no warranties regarding any Event, Business, or Event Coordinator, or your attendance at any Event.
          </p>

          <h2>13. Limitation of Liability</h2>
          <p>
            In no event shall Dangol be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of the Service; the conduct of any Business, Event Coordinator, or Customer; or your attendance at, purchase for, or participation in any Event or Promotion.
          </p>

          <h2>14. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including a breach of these Terms.
          </p>

          <h2>15. Governing Law</h2>
          <p>
            These Terms shall be governed and construed in accordance with the laws of the State of Michigan, United States, without regard to its conflict of law provisions.
          </p>

          <h2>16. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will provide the latest version of these Terms in the App. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.
          </p>

          <h2>17. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at <a href="mailto:support@dangolapp.com">support@dangolapp.com</a>.
          </p>
        </div>

        <div className="legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span> | </span>
          <Link to="/terms-partners">Terms of Service for Partners</Link>
          <span> | </span>
          <Link to="/">Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
